/**
 * Automatic Break Service
 * 
 * Manages automatic break detection and real-time synchronization
 * Monitors Romanian time 12:00-13:00 and broadcasts status changes
 */

import React from 'react';
import { isInAutomaticBreakTime, getBreakStatusMessage } from '../utils/breakUtils';
import { useRealTime } from '../contexts/RealTimeContext';

export interface AutomaticBreakStatus {
  isActive: boolean;
  startTime: Date | null;
  endTime: Date | null;
  message: string;
  timestamp: Date;
}

class AutomaticBreakService {
  public currentStatus: AutomaticBreakStatus | null = null;
  private checkInterval: NodeJS.Timeout | null = null;
  private listeners: Array<(status: AutomaticBreakStatus) => void> = [];
  private isInitialized = false;

  /**
   * Initialize the automatic break monitoring service
   */
  initialize(): void {
    if (this.isInitialized) {
      console.log('AutomaticBreakService: Already initialized');
      return;
    }

    console.log('AutomaticBreakService: Initializing automatic break monitoring');
    
    // Set initial status
    this.updateStatus();
    
    // Check every minute for break status changes
    this.checkInterval = setInterval(() => {
      this.updateStatus();
    }, 60000); // Check every minute
    
    this.isInitialized = true;
    console.log('AutomaticBreakService: Initialization complete');
  }

  /**
   * Stop the automatic break monitoring service
   */
  destroy(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    
    this.listeners = [];
    this.currentStatus = null;
    this.isInitialized = false;
    
    console.log('AutomaticBreakService: Service destroyed');
  }

  /**
   * Update break status and notify listeners if changed
   */
  private updateStatus(): void {
    const isCurrentlyInBreak = isInAutomaticBreakTime();
    const message = getBreakStatusMessage();
    const now = new Date();
    
    // Create new status
    const newStatus: AutomaticBreakStatus = {
      isActive: isCurrentlyInBreak,
      startTime: isCurrentlyInBreak ? this.getBreakStartTime(now) : null,
      endTime: isCurrentlyInBreak ? this.getBreakEndTime(now) : null,
      message,
      timestamp: now
    };

    // Check if status changed
    const statusChanged = this.hasStatusChanged(newStatus);
    
    if (statusChanged) {
      console.log('AutomaticBreakService: Status changed:', {
        wasActive: this.currentStatus?.isActive ?? false,
        nowActive: newStatus.isActive,
        timestamp: newStatus.timestamp.toISOString()
      });
      
      this.currentStatus = newStatus;
      this.notifyListeners(newStatus);
    }
  }

  /**
   * Check if the break status has changed
   */
  private hasStatusChanged(newStatus: AutomaticBreakStatus): boolean {
    if (!this.currentStatus) {
      return true; // First time initialization
    }
    
    return this.currentStatus.isActive !== newStatus.isActive;
  }

  /**
   * Get break start time for today (12:00 Romanian time)
   */
  private getBreakStartTime(referenceTime: Date): Date {
    const romanianTime = new Date(referenceTime.toLocaleString("en-US", { timeZone: "Europe/Bucharest" }));
    const breakStart = new Date(romanianTime);
    breakStart.setHours(12, 0, 0, 0);
    return breakStart;
  }

  /**
   * Get break end time for today (13:00 Romanian time)
   */
  private getBreakEndTime(referenceTime: Date): Date {
    const romanianTime = new Date(referenceTime.toLocaleString("en-US", { timeZone: "Europe/Bucharest" }));
    const breakEnd = new Date(romanianTime);
    breakEnd.setHours(13, 0, 0, 0);
    return breakEnd;
  }

  /**
   * Notify all listeners about status change
   */
  private notifyListeners(status: AutomaticBreakStatus): void {
    console.log(`AutomaticBreakService: Notifying ${this.listeners.length} listeners about break status change`);
    
    this.listeners.forEach((listener, index) => {
      try {
        listener(status);
      } catch (error) {
        console.error(`AutomaticBreakService: Error in listener ${index}:`, error);
      }
    });
  }

  /**
   * Subscribe to automatic break status changes
   */
  subscribe(listener: (status: AutomaticBreakStatus) => void): () => void {
    this.listeners.push(listener);
    
    // Send current status immediately to new listener
    if (this.currentStatus) {
      setTimeout(() => listener(this.currentStatus!), 0);
    }
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Get current break status
   */
  getCurrentStatus(): AutomaticBreakStatus | null {
    return this.currentStatus;
  }

  /**
   * Force a status check (useful for manual refresh)
   */
  forceStatusCheck(): void {
    console.log('AutomaticBreakService: Forcing status check');
    this.updateStatus();
  }

  /**
   * Check if service is initialized
   */
  isServiceInitialized(): boolean {
    return this.isInitialized;
  }
}

// Create singleton instance
export const automaticBreakService = new AutomaticBreakService();

/**
 * React hook for using automatic break service
 */
export const useAutomaticBreak = () => {
  const [status, setStatus] = React.useState<AutomaticBreakStatus | null>(null);
  const realTime = useRealTime();

  React.useEffect(() => {
    // Initialize service if not already initialized
    if (!automaticBreakService.isServiceInitialized()) {
      automaticBreakService.initialize();
    }

    // Subscribe to status changes
    const unsubscribe = automaticBreakService.subscribe((newStatus) => {
      setStatus(newStatus);
      
      // Log break status change for debugging 
      if (realTime.state.isConnected) {
        console.log('AutomaticBreakService: Break status change detected (SignalR connected):', {
          type: newStatus.isActive ? 'AutomaticBreakStarted' : 'AutomaticBreakEnded',
          isActive: newStatus.isActive,
          message: newStatus.message,
          timestamp: newStatus.timestamp.toISOString()
        });
      }
    });

    // Subscribe to backend SignalR events for automatic break status
    if (realTime.state.isConnected) {
      console.log('AutomaticBreakService: Setting up SignalR listeners for backend automatic break events');
      
      // Listen for automatic break events from backend
      const handleAutomaticBreakEvent = (event: { IsActive?: boolean; StartTime?: string; EndTime?: string; Message?: string; Timestamp?: string }) => {
        console.log('AutomaticBreakService: Received automatic break event from backend:', event);
        
        // Update local status based on backend event
        if (event && typeof event.IsActive === 'boolean') {
          const backendStatus: AutomaticBreakStatus = {
            isActive: event.IsActive,
            startTime: event.StartTime ? new Date(event.StartTime) : null,
            endTime: event.EndTime ? new Date(event.EndTime) : null,
            message: event.Message || getBreakStatusMessage(),
            timestamp: new Date(event.Timestamp || new Date())
          };
          
          setStatus(backendStatus);
          automaticBreakService.currentStatus = backendStatus;
        }
      };

      // Subscribe to break events that include automatic break updates
      realTime.subscribeToBreakEvents(handleAutomaticBreakEvent);
      
      // Also listen for system notifications about automatic breaks
      const handleSystemNotification = (notification: { EventName?: string; Data?: unknown }) => {
        if (notification?.EventName === 'AutomaticBreakStatusChanged') {
          console.log('AutomaticBreakService: Received system notification for automatic break:', notification);
          handleAutomaticBreakEvent(notification.Data);
        }
      };
      
      realTime.subscribeToNotifications(handleSystemNotification);
    }

    return () => {
      unsubscribe();
    };
  }, [realTime.state.isConnected, realTime.state.userId, realTime]);

  return {
    status,
    forceCheck: () => automaticBreakService.forceStatusCheck(),
    isInitialized: automaticBreakService.isServiceInitialized()
  };
};

export default automaticBreakService;